/**
 * Meta Pixel — gated, deferred loader.
 *
 * Pixel ID resolution order:
 *   1. import.meta.env.VITE_META_PIXEL_ID  (build-time, if ever provided)
 *   2. /functions/v1/meta-pixel-config     (runtime fetch from Lovable Cloud,
 *                                           which reads the META_PIXEL_ID
 *                                           server-side secret)
 *
 * If neither resolves to a value, every export is a safe no-op.
 *
 * The Pixel ID is a public identifier — it is sent to the browser on every
 * site that uses Meta Pixel. All sensitive Meta values (CAPI access token,
 * page id, etc.) stay server-side and are never exposed here.
 */

import { supabase } from "@/integrations/supabase/client";
import { adsAllowed, onConsentChange } from "@/lib/consent";
import { isProductionAnalyticsHost } from "@/lib/ga4";
import { isMarketingAudience } from "@/lib/marketingAudience";

const BUILD_TIME_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as
  | string
  | undefined;

let resolvedPixelId: string | null = BUILD_TIME_PIXEL_ID || null;
let pixelIdPromise: Promise<string | null> | null = null;

declare global {
  interface Window {
    fbq?: any;
    _fbq?: any;
  }
}

let initialised = false;
let loadingPromise: Promise<void> | null = null;

async function resolvePixelId(): Promise<string | null> {
  if (resolvedPixelId) return resolvedPixelId;
  if (pixelIdPromise) return pixelIdPromise;

  pixelIdPromise = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke<{
        pixelId: string | null;
      }>("meta-pixel-config");
      if (error) return null;
      const id = (data?.pixelId ?? "").toString().trim();
      if (!id) return null;
      resolvedPixelId = id;
      return id;
    } catch {
      return null;
    }
  })();

  return pixelIdPromise;
}

function isEnabled(): boolean {
  // Production marketing hosts only — previews/localhost must not pollute
  // the live Meta dataset (mirrors the GA4 host gate).
  return typeof window !== "undefined" && isMarketingAudience(isProductionAnalyticsHost());
}

let pixelPending = false;

function loadPixel(): Promise<void> {
  if (!isEnabled()) return Promise.resolve();
  if (!adsAllowed()) {
    if (!pixelPending) {
      pixelPending = true;
      const off = onConsentChange((s) => {
        if (s.ad_storage === "granted") {
          off();
          pixelPending = false;
          void loadPixel();
        }
      });
    }
    return Promise.resolve();
  }
  if (initialised) return Promise.resolve();
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const pixelId = await resolvePixelId();
    if (!pixelId) return;

    await new Promise<void>((resolve) => {
      try {
        // Standard Meta Pixel boot sequence.
        /* eslint-disable */
        (function (f: any, b: any, e: any, v: any) {
          if (f.fbq) return;
          const n: any = (f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          });
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = true;
          n.version = "2.0";
          n.queue = [];
          const t = b.createElement(e);
          t.async = true;
          t.src = v;
          t.onload = () => resolve();
          t.onerror = () => resolve(); // never block
          const s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
        /* eslint-enable */

        window.fbq("init", pixelId);
        initialised = true;
      } catch {
        resolve();
      }
    });
  })();

  return loadingPromise;
}

export function metaPixelPageView(): void {
  if (!isEnabled()) return;
  void loadPixel().then(() => {
    try {
      window.fbq?.("track", "PageView");
    } catch {
      /* swallow */
    }
  });
}

export function metaPixelTrack(
  eventName: string,
  params?: Record<string, any>,
): void {
  if (!isEnabled()) return;
  void loadPixel().then(() => {
    try {
      window.fbq?.("track", eventName, params || {});
    } catch {
      /* swallow */
    }
  });
}

export function metaPixelTrackCustom(
  eventName: string,
  params?: Record<string, any>,
): void {
  if (!isEnabled()) return;
  void loadPixel().then(() => {
    try {
      window.fbq?.("trackCustom", eventName, params || {});
    } catch {
      /* swallow */
    }
  });
}

export function metaPixelEnabled(): boolean {
  return isEnabled() && Boolean(resolvedPixelId);
}

